import { observable, computed, reaction } from 'mobx';
import { useRef } from 'react';
import MechaInfoModal from '@src/business-components/modals/mecha-info';
import MechaInfoModalStore from '@src/business-components/modals/mecha-info/store';
import PedigreeChartModal from '@src/business-components/modals/pedigree-chart';
import PedigreeChartModalStore from '@src/business-components/modals/pedigree-chart/store';
import useMount from '@src/utils/hooks/use-mount';
import useModal from '@src/utils/hooks/use-modal';
import { PAGE_ENUM, LEVEL_BLOCK } from './config';
import { getAirmenInfo } from './utils';
import locales from './locales';

class SevenSectionStore {
  constructor(props = {}) {
    Object.assign(this, { ...props });
  }

  @observable isfooterOpen = false;
  @observable level = ''; // 选择的机兵等级 MECHA_LEVEL.PAWN|MECHA_LEVEL.ACE|MECHA_LEVEL.BOSS
  @observable page = ''; // 页面 PAGE_ENUM.LEVEL|PAGE_ENUM.MECHA_TYPE
  @observable mechaIds = [];
  @observable prevMechaInfo = null; // 上一个机兵信息
  @observable isActive = false;
  @observable isMechaActive = false;
  @observable isHidden = true;
  @observable whichActive = null;
  @observable openEntryAnimation = true;
  mechaInfoRef = null;

  // 机兵档案弹窗
  mechaInfoModalStore = new MechaInfoModalStore();
  mechaInfoModal = null;
  // 机兵谱系图弹窗
  pedigreeChartModalStore = new PedigreeChartModalStore();
  pedigreeChartModal = null;

  @computed
  get mechaTypeConfig() {
    const leftMechaInfo = getAirmenInfo(this.level).mechaType?.find(i => i.id === this.mechaIds[1]);
    const rightMechaInfo = getAirmenInfo(this.level).mechaType?.find(i => i.id === this.mechaIds[3]);
    return {
      left: {
        id: leftMechaInfo?.id,
        directive: 'left',
        name: '上一个机兵',
        normal: leftMechaInfo?.blockLeft,
        hover: leftMechaInfo?.blockLeftHover
      },
      right: {
        id: rightMechaInfo?.id,
        directive: 'right',
        name: '下一个机兵',
        normal: rightMechaInfo?.blockRight,
        hover: rightMechaInfo?.blockRightHover
      }
    };
  }

  @computed
  get mechaInfo() {
    return getAirmenInfo(this.level).mechaType?.find(i => i.id === this.mechaIds[2]);
  }

  onViewMechaInfo = () => {
    this.mechaInfoModalStore.mechaInfo = this.mechaInfo;
    this.mechaInfoModal.open();
  };
  onViewAllPedigreeChart = () => {
    this.pedigreeChartModalStore.level = this.level;
    this.pedigreeChartModal.open();
  };

  onBlockHover = cfg => {
    if (this.page === PAGE_ENUM.LEVEL) {
      this.whichActive = cfg;
      this.level = cfg.level;
    }
  };

  onTogglePage = (page, level) => {
    this.page = page;

    if (page === PAGE_ENUM.LEVEL) {
      this.prevMechaInfo = null;
    }

    if (level) {
      this.level = level;
      const mechaType = getAirmenInfo(level).mechaType;
      let first = null,
        second = mechaType[mechaType.length - 1];
      if (mechaType.length > 1) {
        first = mechaType[mechaType.length - 2];
      } else {
        first = second;
      }
      const mechaIds = [first?.id, second?.id, ...mechaType.slice(0, 3).map(i => i.id)];
      while (mechaIds.length < 5) {
        mechaIds.push(mechaIds[0]);
      }
      this.startAni(() => {
        this.mechaIds = mechaIds;
      }).then(() => {
        this.prevMechaInfo = this.mechaInfo;
      });
    }
  };

  getMechaId = (curId, directive = 'prev') => {
    const mechaType = getAirmenInfo(this.level).mechaType;
    const mechaIndex = mechaType.findIndex(i => i.id === curId);
    if (mechaIndex === -1) return;
    let mecha = mechaType[mechaIndex + (directive === 'prev' ? -1 : 1)];
    if (mecha) {
      return mecha.id;
    } else if (directive === 'prev') {
      return mechaType[mechaType.length - 1].id;
    } else {
      return mechaType[0].id;
    }
  };

  startAni = cb => {
    if (this.isMechaActive) {
      return Promise.reject();
    }
    return new Promise(resolve => {
      cb?.();
      this.openEntryAnimation = false;
      this.isMechaActive = true;
      this.isHidden = false;
      setTimeout(() => {
        this.isHidden = true;
        this.openEntryAnimation = true;
      }, 0);
      const listener = () => {
        resolve();
        this.isMechaActive = false;
        this.mechaInfoRef.current.removeEventListener('animationend', listener);
      };
      this.mechaInfoRef.current.addEventListener('animationend', listener);
    });
  };

  onPrevMecha = () => {
    this.startAni(() => {
      this.mechaIds = [
        this.getMechaId(this.mechaIds[0]),
        this.getMechaId(this.mechaIds[1]),
        this.getMechaId(this.mechaIds[2]),
        this.getMechaId(this.mechaIds[3]),
        this.getMechaId(this.mechaIds[4])
      ];
    }).then(() => {
      this.prevMechaInfo = this.mechaInfo;
    });
  };

  onNextMecha = () => {
    this.startAni(() => {
      this.isSwitching = false;
      this.oldMechaIds = this.mechaIds.slice();
      this.mechaIds = [
        this.getMechaId(this.mechaIds[0], 'next'),
        this.getMechaId(this.mechaIds[1], 'next'),
        this.getMechaId(this.mechaIds[2], 'next'),
        this.getMechaId(this.mechaIds[3], 'next'),
        this.getMechaId(this.mechaIds[4], 'next')
      ];
    }).then(() => {
      this.prevMechaInfo = this.mechaInfo;
    });
  };

  toggleMechaInfo = type => {
    if (type === 'prev') {
      this.mechaTypeBlockRef.current?.onClick(this.mechaTypeConfig.left);
    } else {
      this.mechaTypeBlockRef.current?.onClick(this.mechaTypeConfig.right);
    }
  };

  /**
   * 修改footer的显隐状态
   * @param newState
   */
  onChangeFooterState = newState => {
    this.footer.isActive = newState;
    this.isfooterOpen = newState;
    // console.log(this.isfooterOpen);
  };

  reset() {
    this.isfooterOpen = false;
    this.level = ''; // 选择的机兵等级 MECHA_LEVEL.PAWN|MECHA_LEVEL.ACE|MECHA_LEVEL.BOSS
    this.page = ''; // 页面 PAGE_ENUM.LEVEL|PAGE_ENUM.MECHA_TYPE
    this.mechaIds = [];
    this.prevMechaInfo = null; // 上一个机兵信息
    this.isActive = false;
    this.isMechaActive = false;
    this.isHidden = true;
    this.whichActive = null;
    this.openEntryAnimation = true;
  }

  use() {
    this.mechaInfoRef = useRef();
    this.mechaTypeBlockRef = useRef();

    // 机兵档案弹窗
    this.mechaInfoModal = useModal(MechaInfoModal, { store: this.mechaInfoModalStore, locales });
    // 机兵谱系图弹窗
    this.pedigreeChartModal = useModal(PedigreeChartModal, { store: this.pedigreeChartModalStore, locales });

    useMount(() => {
      reaction(
        () => this.isActive,
        isActive => {
          if (isActive) {
            this.onTogglePage(PAGE_ENUM.LEVEL);
            this.onBlockHover(LEVEL_BLOCK.left);
          } else {
            this.reset();
          }
        },
        {
          fireImmediately: true
        }
      );
      // this.onViewAllPedigreeChart();
    });
  }
}

export default SevenSectionStore;
